import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat.js"
import moment from "moment"

// Needed so dayjs(value, "DD-MM-YYYY") actually honours the format instead of
// falling back to loose parsing. Without it every non-ISO field format silently
// mis-parses.
dayjs.extend(customParseFormat)

export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD"
export const DEFAULT_TIME_FORMAT = "HH:mm"

// Date values reach us in a lot of shapes: ISO strings from the API, Date objects
// from integrations, live dayjs objects straight out of a picker, formatted
// strings ("15-01-2024") and - the one that actually breaks things - dayjs objects
// that lost their prototype after a JSON / redux / IndexedDB round trip. Those
// still pass dayjs.isDayjs, so dayjs(value) takes the "already a dayjs" branch and
// calls value.clone(), which throws "t.clone is not a function".
//
// Everything in the app that formats, compares or renders a date value should go
// through these helpers rather than calling dayjs()/moment() on the value directly.

// A dayjs that kept its shape ($d / $isDayjsObject) but not its methods.
const isPrototypelessDayjs = (value) =>
    value !== null &&
    typeof value === "object" &&
    typeof value.clone !== "function" &&
    (value.$isDayjsObject === true || value.$d !== undefined)

const rebuildPrototypelessDayjs = (value) => {
    const raw = value.$d
    if (raw instanceof Date) {
        return dayjs(raw)
    }
    if (typeof raw === "string" || typeof raw === "number") {
        return dayjs(raw)
    }
    // Some serializers keep only the parts, not $d.
    if (typeof value.$y === "number" && typeof value.$M === "number") {
        return dayjs(new Date(value.$y, value.$M, value.$D || 1, value.$H || 0, value.$m || 0, value.$s || 0))
    }
    return dayjs(null)
}

/**
 * Coerce any stored value into a real dayjs. Never throws; returns an invalid
 * dayjs when the value cannot be understood, so `.isValid()` / `.format()` are
 * always safe to call on the result.
 *
 * When `format` is given it is tried first in strict mode (that is how the field
 * renders and round-trips its value, e.g. "15-01-2024" for DD-MM-YYYY), then we
 * fall back to loose parsing so ISO values stored on the same field still work.
 */
export const safeDayjs = (value, format) => {
    if (value === null || value === undefined || value === "") {
        return dayjs(null)
    }
    if (value instanceof Date) {
        return dayjs(value)
    }
    if (dayjs.isDayjs(value)) {
        if (typeof value.clone === "function") {
            return value
        }
        return rebuildPrototypelessDayjs(value)
    }
    if (moment.isMoment && moment.isMoment(value)) {
        return dayjs(value.toDate())
    }
    if (isPrototypelessDayjs(value)) {
        return rebuildPrototypelessDayjs(value)
    }
    if (typeof value === "object") {
        // A plain object is not a date - don't let dayjs guess at it.
        return dayjs(null)
    }
    if (format) {
        const formats = Array.isArray(format) ? format : [format]
        for (const fmt of formats) {
            if (!fmt) continue
            const byFormat = dayjs(value, fmt, true)
            if (byFormat.isValid()) {
                return byFormat
            }
        }
    }
    return dayjs(value)
}

/**
 * Value for an antd DatePicker / TimePicker: a valid dayjs, or null so the picker
 * renders empty instead of "Invalid Date".
 */
export const toDatePickerValue = (value, format) => {
    const parsed = safeDayjs(value, format)
    return parsed.isValid() ? parsed : null
}

/** Format any stored date value for display. Returns `fallback` when unusable. */
export const formatDate = (value, format = DEFAULT_DATE_FORMAT, fallback = "") => {
    const parsed = safeDayjs(value, format)
    return parsed.isValid() ? parsed.format(format) : fallback
}

/** True when the value can be understood as a real point in time. */
export const isRealDate = (value) => safeDayjs(value).isValid()

/**
 * True when the value is a date *object* - a Date, a moment, or a dayjs including
 * one that lost its prototype. Use this instead of moment.isMoment / dayjs.isDayjs
 * when a stored value could be any of them.
 */
export const isDateObject = (value) => {
    if (value === null || typeof value !== "object") {
        return false
    }
    return value instanceof Date
        || dayjs.isDayjs(value)
        || moment.isMoment(value)
        || isPrototypelessDayjs(value)
}

/**
 * moment() equivalent of safeDayjs, for the screens still on moment. Passing a
 * dayjs object (prototype-less or not) straight to moment() yields Invalid Date.
 */
export const safeMoment = (value) => {
    if (value === null || value === undefined || value === "") {
        return moment(null)
    }
    if (moment.isMoment(value)) {
        return value
    }
    if (value instanceof Date) {
        return moment(value)
    }
    if (dayjs.isDayjs(value) || isPrototypelessDayjs(value)) {
        const asDayjs = safeDayjs(value)
        return asDayjs.isValid() ? moment(asDayjs.toDate()) : moment(null)
    }
    if (typeof value === "object") {
        return moment(null)
    }
    return moment(value)
}

/**
 * Normalise a date value for storage / transport: an ISO string, or the value
 * untouched when it is not a date. Use before sending form values to the API so
 * prototype-less dayjs objects never leave the client as `{}`.
 */
export const toISOStringSafe = (value, fallback = null) => {
    const parsed = safeDayjs(value)
    return parsed.isValid() ? parsed.toISOString() : fallback
}

export default safeDayjs
